import { useFormContext } from "react-hook-form";
import { Trash2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { HandbookFormValues } from "@/lib/validation/handbook-schema";

interface HandbookFootnotesFieldProps {
  /**
   * "+ 각주" 버튼 클릭 핸들러. ChangelogEditor 의 onInsertFootnote 와 같은 책임:
   * 새 footnote row 발급 + 본문 cursor 위치에 sup 마커 인서트.
   * editor instance 가 부모에 노출되어 있어야 하므로 부모(HandbookEditorPanel) 가 주입.
   */
  onAddFootnote?: () => void;
}

/**
 * 각주 입력 영역.
 *
 * - 본문 cursor 위치에 마커 인서트 → 새 row 자동 푸시 (HandbookEditorPanel 의
 *   onInsertFootnote → ChangelogEditor → editor.commands.insertFootnote 흐름)
 * - 본문 텍스트 선택 후 bubble menu 의 ✱ 버튼 클릭으로도 추가 가능
 * - 이 영역의 row 삭제는 footnotes 배열에서만 제거. 본문 sup 마커가 남으면 위키에서
 *   [?] 회색으로 표시되므로 본문에서도 직접 정리하도록 안내
 */
export function HandbookFootnotesField({
  onAddFootnote,
}: HandbookFootnotesFieldProps) {
  const form = useFormContext<HandbookFormValues>();
  const footnotes = form.watch("footnotes") ?? [];

  const removeAt = (index: number) => {
    const next = footnotes.filter((_, i) => i !== index);
    form.setValue("footnotes", next, { shouldValidate: true });
  };

  const setContentAt = (index: number, content: string) => {
    const next = footnotes.map((fn, i) =>
      i === index ? { ...fn, content } : fn,
    );
    form.setValue("footnotes", next, { shouldValidate: true });
  };

  return (
    <div className="space-y-3 rounded-md border bg-muted/20 p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">각주 ({footnotes.length})</h3>
        {onAddFootnote && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onAddFootnote}
            className="h-7 gap-1 px-2 text-xs"
          >
            <Plus className="h-3.5 w-3.5" />
            각주 추가
          </Button>
        )}
      </div>

      {footnotes.length === 0 ? (
        <p className="rounded-md border border-dashed bg-background/60 px-3 py-3 text-xs text-muted-foreground">
          본문에서 텍스트를 선택하고 bubble menu 의 ✱ 버튼을 누르거나, 위{" "}
          <strong>각주 추가</strong> 버튼을 눌러 시작하세요. 추가된 각주는
          본문에서 마커 위치를 자유롭게 옮길 수 있습니다.
        </p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">
            본문의 마커를 지우면 아래 목록에서도 함께 삭제하세요.
          </p>
          <ol className="space-y-2">
            {footnotes.map((fn, i) => (
              <li
                key={fn.id}
                className="flex items-start gap-2 rounded-md border bg-background p-2"
              >
                <span className="mt-1 inline-flex h-6 min-w-[28px] items-center justify-center rounded bg-muted px-1.5 text-xs font-mono">
                  [{i + 1}]
                </span>
                <textarea
                  value={fn.content}
                  onChange={(e) => setContentAt(i, e.target.value)}
                  placeholder="각주 내용 (HTML 가능 — 본문과 같은 sanitize 통과)"
                  rows={2}
                  className="flex-1 resize-y rounded border border-input bg-background px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeAt(i)}
                  aria-label={`${i + 1}번 각주 삭제`}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ol>
          {form.formState.errors.footnotes && (
            <p className="text-xs text-destructive">
              {form.formState.errors.footnotes.message ??
                "각주에 입력 오류가 있습니다"}
            </p>
          )}
        </>
      )}
    </div>
  );
}
